import {put, call} from 'redux-saga/effects';
import CaseTypeStage from "../../entities/caseTypeStage";
import * as Actions from "../actions/caseTypeStageActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CaseTypeStageAction = {
    caseTypeStageId: string;
    caseTypeStage: CaseTypeStage;
    caseTypeStages: CaseTypeStage[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCaseTypeStage(action: CaseTypeStageAction) {
	const session = tabSession.captureGeneration();
	try {
		const caseTypeStage: CaseTypeStage = yield call(api.findOne, 'caseTypeStage', action.caseTypeStageId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_FETCH_ONE_SUCCESS, caseTypeStageId: action.caseTypeStageId, caseTypeStage});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPESTAGE_FETCH_ONE_FAILURE, caseTypeStageId: action.caseTypeStageId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CaseTypeStageAction} action
 */
export function* saveCaseTypeStage(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ CASETYPESTAGE_SAVE saga', action.caseTypeStage?.id);
    
	if (action.caseTypeStage === undefined || action.caseTypeStage === null) {
		console.error('saveCaseTypeStage received a null');
		throw new Error('saveCaseTypeStage received a null');
	}

	if (!(action.caseTypeStage instanceof CaseTypeStage)) {
		action.caseTypeStage = new CaseTypeStage(action.caseTypeStage);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'caseTypeStage', action.caseTypeStage);
		if (addResult.isErr()) {
			console.error(`⮂ saveCaseTypeStage: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.CASETYPESTAGE_SAVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_SAVE, caseTypeStage: action.caseTypeStage});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.CASETYPESTAGE_SAVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CaseTypeStageAction} action
 * @param {CaseTypeStage[]} action.caseTypeStages
 */
export function* saveManyCaseTypeStages(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ CASETYPESTAGE_SAVE_MANY saga', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));
    
    if (!Array.isArray(action.caseTypeStages)) {
        console.error('saveManyCaseTypeStages expected an array, received', action.caseTypeStages);
        throw new Error('saveManyCaseTypeStages expected an array');
    }

	try {
		const toSave: CaseTypeStage[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.caseTypeStages.length; i++) {
			const caseTypeStage = action.caseTypeStages[i];
			caseTypeStage.persisted = false;
			toSave.push(caseTypeStage);
		}

		const addResult = yield call(idb.add, 'caseTypeStage', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyCaseTypeStages: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.CASETYPESTAGE_SAVE_MANY_FAILURE, caseTypeStages: action.caseTypeStages, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_SAVE_MANY, caseTypeStages: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.CASETYPESTAGE_SAVE_MANY_FAILURE, caseTypeStages: action.caseTypeStages, status: 500, err});

		console.error(`Error saving ${action.caseTypeStages.length} caseTypeStages to database`, err);
	}
}

/**
 * @param {CaseTypeStageAction} action
 */
export function* removeCaseTypeStage(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.caseTypeStage === undefined || action.caseTypeStage === null || !(action.caseTypeStage instanceof CaseTypeStage)) {
			console.error('removeCaseTypeStage expected type CaseTypeStage, received', action.caseTypeStage);
			throw new Error('removeCaseTypeStage expected type CaseTypeStage');
		}

	try {
		// Mark as deleted
		action.caseTypeStage.status = 'D';
		action.caseTypeStage.addedOn = newHRTimestamp();
		action.caseTypeStage.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'caseTypeStage', action.caseTypeStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCaseTypeStage: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPESTAGE_REMOVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_REMOVE, caseTypeStage: action.caseTypeStage});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPESTAGE_REMOVE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this CaseTypeStage.
	*
	* @param {CaseTypeStageAction} action
	*/
export function* restoreCaseTypeStage(action: CaseTypeStageAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCaseTypeStage expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.caseTypeStage === undefined || action.caseTypeStage === null) {
        console.error('restoreCaseTypeStage expected a CaseTypeStage, received null');
        throw new Error('restoreCaseTypeStage expected a CaseTypeStage, received null');
    }
    else if (!(action.caseTypeStage instanceof CaseTypeStage)) action.caseTypeStage = new CaseTypeStage(action.caseTypeStage); 
    
	try {
		// Mark as restored
		action.caseTypeStage.status = 'U';
		action.caseTypeStage.addedOn = newHRTimestamp();
		action.caseTypeStage.addedBy = userId;
		action.caseTypeStage.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'caseTypeStage', action.caseTypeStage);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCaseTypeStage: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPESTAGE_RESTORE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPESTAGE_RESTORE, caseTypeStage: action.caseTypeStage});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CASETYPESTAGE_RESTORE_FAILURE, caseTypeStage: action.caseTypeStage, status: 500, err});

		console.error('Error restoring from database', err);
	}
}